import JsonLd from "@/components/JsonLd.tsx";
import JsonLdBreadcrumbList from "@/components/JsonLdBreadcrumbList.tsx";
import {config} from "@/config/config.ts";
import FaqComponent from "@/landing/sections/FaqComponent.tsx";
import {createFileRoute, Link} from '@tanstack/react-router'
import React from "react";
import ctaBg from '../landing/assets/about_us_4.webp';
import bracketsCtaBg from '../landing/assets/brackets_cta_bg.webp';
import iconApple from '../landing/assets/Apple.png';
import iconGoogle from '../landing/assets/Playstore.png';
import bracketsFeaturesBg from '../landing/assets/brackets_features_bg.webp';
import bracketsHeroBg from '../landing/assets/brackets_hero_bg.webp';
import bracketsIcon1 from '../landing/assets/svgs/brackets_icon_1.svg';
import bracketsIcon2 from '../landing/assets/svgs/brackets_icon_2.svg';
import bracketsIcon3 from '../landing/assets/svgs/brackets_icon_3.svg';
import bracketsIcon4 from '../landing/assets/svgs/brackets_icon_4.svg';
import LandingNav from "../landing/components/LandingNav.tsx";
import ArrowUpRightIcon from "../landing/icons/ArrowUpRightIcon.tsx";
import CheckBadgeIcon from "../landing/icons/CheckBadgeIcon.tsx";
import FooterSection from "../landing/sections/FooterSection.tsx";

const bracketSteps = [
  {
    title: "Add Players or Teams",
    description:
      "Enter participant names manually or upload your list to begin creating your tournament.",
  },
  {
    title: "Choose Tournament Format",
    description:
      "Select from single elimination, double elimination, or round robin based on your event needs.",
  },
  {
    title: "Generate Bracket Instantly",
    description:
      "The tournament bracket software automatically creates matchups and structures your bracket in seconds.",
  },
  {
    title: "Schedule Matches",
    description:
      "Assign match timings, venues, and rounds to keep your tournament organized.",
  },
  {
    title: "Track Results in Real-Time Match Scoring",
    description:
      "Experience real-time match scoring by updating scores and following progress easily with live updates and live scoring, ensuring a smooth tournament flow from start to finish.",
  },
];

const FAQS = [
    {
        q: 'How do I create tournament brackets using ScorePal?',
        a: <>You simply choose your format, add your teams, and the bracket generator automatically builds your structure. With ScorePal, you can create brackets for tournaments online in seconds without using manual templates or spreadsheets.</>,
    },
    {
        q: 'What makes ScorePal a good tournament bracket generator?',
        a: <>ScorePal is a fast and reliable tournament bracket software designed for ease of use. It simplifies the entire process from adding participants to managing matches so you can focus on running your event instead of building brackets manually.</>,
    },
    {
        q: 'Can I customize my tournament bracket?',
        a: <>Yes. This flexible tournament bracket software allows you to customize team names, seeds, match timings, colors, and more. You can easily create a tournament bracket online that match your event’s exact requirements.</>,
    },
    {
        q: 'Do I need any technical skills to build a bracket?',
        a: <>No technical skills are required. ScorePal’s intuitive bracket generator is built for beginners and professionals alike. You can quickly create brackets for tournaments online by simply entering team details.</>,
    },
    {
        q: 'What types of brackets can I make with the online tournament bracket maker?',
        a: <>You can create single-elimination, double-elimination, round robin, or mixed-format brackets. The tournament bracket software automatically organizes matchups to ensure a smooth tournament flow.</>,
    },
    {
        q: 'Can I print or share my tournament bracket?',
        a: <>Yes. ScorePal lets you download print-ready brackets or share them digitally. This bracket generator also supports live updates, making it easy to keep everyone informed in real time.</>,
    },
];

export const Route = createFileRoute('/create-brackets')({
    component: RouteComponent,
})

function RouteComponent() {
    const features = [
        {
            icon:        bracketsIcon1,
            alt: 'brackets icon',
            title:       'Instant Bracket Creation',
            description: 'Build flexible single-elimination, double-elimination, or round robin tournaments in just a few clicks with our powerful tournament bracket generator. The platform simplifies bracket creation and uses intelligent logic for automated matchups, ensuring your tournament structure is accurate from the start.',
        },
        {
            icon:        bracketsIcon2,
            alt: 'brackets icon',
            title:       'Printable & Shareable',
            description: 'Use ScorePal as your all-in-one bracket maker software to download print-ready PDFs or share live, interactive brackets online. With real-time live updates, participants and audiences can easily follow match results and progress.',
        },
        {
            icon:        bracketsIcon3,
            alt: 'brackets icon',
            title:       'Fully Customizable Designs',
            description: 'This advanced tournament bracket software lets you customize every detail from team names and seeds to match timings, logos, and colors, while maintaining a smooth and organized tournament flow throughout your event.',
        },
        {
            icon:        bracketsIcon4,
            alt: 'brackets icon',
            title:       'Ideal for Schools, Clubs & Sports Organizations',
            description: 'ScorePal’s bracket maker is built for versatility: perfect for athletic departments, community centers, camps, eSports, and recreational groups looking for a simple, reliable solution. It also works as a powerful sports league organizer platform, making it ideal for managing structured competitions at any level.',
        },
        {
            icon:        bracketsIcon1,
            alt: 'brackets icon',
            title:       'Flexible for Any Tournament Format',
            description: 'Whether your organizing school competitions, sports leagues, or eSports events, ScorePal works as a flexible tournament bracket generator that supports seamless bracket creation, structured match progression, and consistent tournament flow across all formats.',
        },
        {
            icon:        bracketsIcon2,
            alt: 'brackets icon',
            title:       'All-in-One Tournament Management',
            description: 'More than just a bracket tool, ScorePal combines online bracket maker software capabilities with advanced tournament flow management, automated updates, and score tracking. It also supports efficient league management, helping you manage every stage of your event effortlessly.',
        },
    ];

   const whyChooseItems = [
  'Fast, intuitive creation process',
  'Supports multiple tournament formats',
  'Printable and digital brackets',
  'Clean, professional layouts',
  'Perfect for USA-based sports and school events',
  'Automated matchups and smart bracket structuring',
  (
    <span>
      Supports multiple formats and works well with{" "}
      <a
        href="https://scorepal.co/sports-league-management-software/"
        className="not-hover:underline"
      >
        sports league software
      </a>
    </span>
  ),
];

    return (
        <div className="min-h-screen bg-[#EDEEF4]">
            <JsonLd id={`page${Route.id}/faq-schema`} item={{
                "@context":   "https://schema.org",
                "@type":      "FAQPage",
                "mainEntity": [
                    {
                        "@type":          "Question",
                        "name":           "How do I create tournament brackets using ScorePal?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text":  "You just choose your bracket style, add your teams, and ScorePal builds it for you automatically. The tool is designed to help anyone create tournament brackets quickly without dealing with templates or manual layouts."
                        }
                    },
                    {
                        "@type":          "Question",
                        "name":           "What makes ScorePal a good tournament bracket generator?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text":  "ScorePal is a fast, easy-to-use online tournament bracket maker and works for many types of events. Everything happens in a few simple steps, which saves time and effort. Whether you’re managing a small event or handling more players, ScorePal helps you organize every stage from participants to the final match."
                        }
                    },
                    {
                        "@type":          "Question",
                        "name":           "Can I customize my tournament bracket?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text":  "Yes. ScorePal works as a custom tournament bracket maker, so you can edit team names, seeds, match timings, colors, and more. This helps you create a bracket that fits your event perfectly."
                        }
                    },
                    {
                        "@type":          "Question",
                        "name":           "Do I need any technical skills to build a bracket?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text":  "Not at all. ScorePal is built for everyone—teachers, coaches, and event organizers. If you can type in the team names, you can create a full tournament bracket without trouble. It lets you build brackets effortlessly while keeping every game clearly displayed with all required details."
                        }
                    },
                    {
                        "@type":          "Question",
                        "name":           "What types of brackets can I make with the online tournament bracket maker?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text":  "You can build single-elimination, double-elimination, round robin, or mixed-format brackets. The system arranges everything for you so your bracket always looks neat and professional."
                        }
                    },
                    {
                        "@type":          "Question",
                        "name":           "Can I print or share my tournament bracket?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text":  "Yes. ScorePal lets you download a print-ready version or share an interactive link online. This makes it easy to update scores or let others follow the bracket in real time."
                        }
                    }
                ]
            }} />
            <JsonLd id={`page${Route.id}/app`} item={{
                "@context": "https://schema.org",
                "@graph": [
                    {
                        "@type": "SoftwareApplication",
                        "name": "Bracket Generator",
                        "description": "Easily create and manage tournament brackets with automated scheduling, player seeding, and real-time match updates using ScorePal. Proven performance over the past 2 years.",
                        "url": "https://scorepal.co/create-brackets/",
                        "applicationCategory": "SportsApplication",
                        "operatingSystem": ["Web", "Android", "iOS"],
                        "offers": {
                            "@type": "Offer",
                            "price": "0",
                            "priceCurrency": "USD"
                        }
                    },
                    {
                        "@type": "HowTo",
                        "name": "How to Create Tournament Brackets",
                        "step": [
                            {
                                "@type": "HowToStep",
                                "text": "Add players"
                            },
                            {
                                "@type": "HowToStep",
                                "text": "Generate bracket"
                            },
                            {
                                "@type": "HowToStep",
                                "text": "Start matches"
                            }
                        ]
                    }
                ]
            }} />
            <JsonLdBreadcrumbList routeId={Route.id} crumbs={[{name: "Create Brackets", item: "https://scorepal.co/create-brackets/"}]} />
            <title>Create Tournament Brackets Online | Free Bracket Generator | ScorePal</title>
            <meta name="description" content="Create tournament brackets instantly with an easy bracket generator. Build, manage, and share brackets for any sport or competition in seconds." />
            <link rel="canonical" href={`${config.publicUrl}/create-brackets/`} />

            <section className="relative overflow-hidden p-4">
                <div
                    className="flex flex-col min-h-screen justify-between relative pb-16 rounded-4xl bg-[#EDEEF4] bg-center bg-cover bg-no-repeat saturate-[0.87]"
                    style={{
                        backgroundImage: `url(${bracketsHeroBg})`
                    }}
                >
                    <div className="absolute inset-0 bg-linear-to-r from-black/60 via-black/30 to-transparent rounded-4xl" />

                    <div className="relative z-20">
                        <LandingNav />
                    </div>

                    <div className="container mx-auto px-6 lg:px-12 pt-8 flex-1 flex flex-col justify-center relative z-10">
                        <h1 className="font-bebas text-white uppercase text-[clamp(40px,6vw,72px)] leading-none font-bold tracking-wide mb-4">
                            Create Tournament Brackets online
                        </h1>

                        <p className="font-inter text-white/90 text-base lg:text-xl mb-8 max-w-2xl leading-relaxed">
                            Build, manage, and share brackets for any competition in seconds
                        </p>

                        <div className="flex flex-wrap items-center gap-3">
                            <Link
                                to={"/signup/player/" as any}
                                className="flex items-center gap-2 bg-[#EDEEF4] text-[#082F8A] rounded-full pl-6 pr-1 py-1 w-fit font-semibold hover:bg-white transition"
                            >
                                <span className="font-inter text-base font-medium">Lets Get Started</span>
                                <div className="bg-[#082F8A] rounded-full p-1">
                                    <ArrowUpRightIcon className="text-white" size={28} />
                                </div>
                            </Link>
                            <Link
                                to="/find-tournaments/"
                                className="rounded-full border border-white/40 px-6 py-3 text-base font-semibold text-white transition hover:bg-white/10"
                            >
                                Browse Tournaments
                            </Link>
                        </div>

                        <div className="flex flex-wrap gap-4 mt-16 lg:mt-60">
                        <a
                        href="https://play.google.com/store/apps/details?id=co.scorepal.mobile&pcampaignid=web_share"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block"
                            >
                        <div className="bg-white/10 rounded-xl px-6 py-3 flex items-center gap-3 hover:bg-gray-100 transition">
                        <img src={iconGoogle} alt="Google Play" className="h-8" />
                        <div className="text-left">
                        <div className="text-white/70 text-xs">GET IT ON</div>
                        <div className="text-white font-semibold text-sm">Google Play</div>
                        </div>
                    </div>
                    </a>

                    <a
                    href="https://apps.apple.com/us/app/score-pal/id6745509167"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block"
                    >
                    <div className="bg-white/10 rounded-xl px-6 py-3 flex items-center gap-3 hover:bg-gray-900 transition">
                    <img src={iconApple} alt="App Store" className="h-8" />
                    <div className="text-left">
                    <div className="text-white/70 text-xs">Download on the</div>
                <div className="text-white font-semibold text-sm">App Store</div>
            </div>
        </div>
    </a>
</div>
                    </div>
                </div>
            </section>

            <section className="py-16 lg:py-24 bg-[#EDEEF4]">
                <div className="container mx-auto px-6 lg:px-12">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-start">
                        <div>
                            <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-6">
                                FAST, SIMPLE, AND SHAREABLE<br />BRACKETS <span className="text-[#082F8A]"> FOR ANY EVENT</span>
                            </h2>

                            <p className="font-inter text-[#1a1a2e]/80 text-base leading-relaxed">
                                Create clean, professional tournament brackets in seconds with ScorePal’s custom tournament online bracket generator. Managing tournaments with manual brackets, spreadsheets, and unclear match structures often leads to confusion, errors, and seeding issues. Perfect for sports events, school competitions, and club tournaments across the USA, ScorePal makes it easy to create tournament brackets and design, customize, print, and share brackets for any format or size using reliable{" "}
                            <a
                                href="https://scorepal.co/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#082F8A] underline font-semibold"
                                >
                                online tournament software
                                </a>
                                </p>
                        </div>

                        <div className="hidden lg:block">
                            <img
                                src={bracketsFeaturesBg}
                                alt="Tournament brackets"
                                className="w-full h-auto rounded-3xl object-cover"
                            />
                        </div>
                        </div>
                    </div>
                 </section>

                    <section className="py-16 lg:py-24 bg-[#EDEEF4]">
                    <div className="container mx-auto px-6 lg:px-12">

                        <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-8 text-center">
                            Key Features of <span className="text-[#082F8A]">ScorePal</span> Tournament Brackets
                        </h2>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-12">
                        {features.map((feature, index) => (
                        <div key={index} className="bg-white rounded-2xl p-6 shadow-sm">
                        <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-full bg-[#EDEEF4] flex items-center justify-center shrink-0">
                            <img src={feature.icon} alt="" className="w-6 h-6" />
                        </div>

                        <h3 className="font-inter text-[#082F8A] font-semibold text-base">
                            {feature.title}
                        </h3>
                    </div>

                    <p className="font-inter text-[#1a1a2e]/70 text-sm leading-relaxed">
                        {feature.description}
                    </p>
                        </div>
                            ))}
                        </div>
                    </div>
                </section>

                 <div className="flex justify-center">
            <Link
            to={"/signup/player/" as any}
            className="flex items-center gap-2 bg-[#EDEEF4] text-[#082F8A] rounded-full pl-6 pr-1 py-1 w-fit font-semibold hover:bg-white transition"
            >
            <span className="font-inter text-base font-medium">Lets Get Started</span>
            <div className="bg-[#082F8A] rounded-full p-1">
            <ArrowUpRightIcon className="text-white" size={28} />
            </div>
            </Link>
            </div>

            <section className="py-16 lg:py-24 bg-[#EDEEF4]">
                <div className="container mx-auto px-6 lg:px-12">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
                        <div>
                            <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-8">
                                Why Is ScorePal the Best Tool to Create Tournament Brackets?
                            </h2>

                                <p className="font-inter text-[#1a1a2e]/80 text-base leading-relaxed mb-8">
                            ScorePal is a powerful bracket generator for tournaments designed to simplify planning, creation, and management for events of any size. It supports all tournament formats (single elimination, double elimination, and round robin), making it a flexible solution for any competition.
                            </p>

                            <ul className="space-y-4">
                                {whyChooseItems.map((item, index) => (
                                    <li key={index} className="flex items-center gap-4">
                                        <CheckBadgeIcon size={24} />
                                        <span className="font-inter text-[#1a1a2e] text-base">
                                            {item}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="hidden lg:block">
                            <img
                                src={bracketsCtaBg}
                                alt="Padel player"
                                className="w-full h-auto rounded-3xl object-cover"
                            />
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-8 lg:py-16">
                <div className="container mx-auto px-6 lg:px-12">
                    <div
                        className="rounded-3xl py-16 lg:py-24 px-8 text-center bg-top bg-cover bg-no-repeat"
                        style={{backgroundImage: `url(${ctaBg})`}}
                    >
                        <h2 className="font-bebas text-white uppercase text-[clamp(28px,4vw,40px)] leading-tight font-bold tracking-wide mb-4">
                            Create Tournament Brackets in 5 Simple Steps
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
                                {bracketSteps.map((step, index) => (
                              <div
                                key={index}
                                className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10"
                                  >
                        <div className="w-10 h-10 rounded-full bg-white text-[#082F8A] flex items-center justify-center font-bold mb-4">
                          {index + 1}
                        </div>

            <h3 className="font-inter text-white font-semibold text-base mb-3">
              {step.title}
            </h3>

            <p className="font-inter text-white/80 text-sm leading-relaxed">
              {step.description}
            </p>
          </div>
        ))}
      </div>

                        <div className="flex justify-center mt-12">
                          <Link
                            to={"/signup/player/" as any}
                            className="inline-flex items-center gap-2 bg-white text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-gray-100 transition"
                            >
                          <span className="font-inter text-base font-medium">
                            Lets Get Started
                          </span>

                          <div className="bg-[#082F8A] rounded-full p-1">
                          <ArrowUpRightIcon className="text-white" size={28} />
                          </div>
                          </Link>
                          </div>

                          </div>
                  </div>
                  </section>

                  <section className="py-16 lg:py-24 bg-white">
            <div className="container mx-auto px-6 lg:px-12">

            <div className="text-center max-w-5xl mx-auto mb-12">
            <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-6">
                Manual vs Software: Why Choose a Bracket Generator?
            </h2>

            <p className="font-inter text-[#1a1a2e]/80 text-base leading-relaxed">
                Managing tournaments manually can be time-consuming and error-prone. Here’s how ScorePal compares:
            </p>
        </div>

        <div className="overflow-x-auto">
            <div className="min-w-[800px] border border-[#d9dce7] rounded-3xl overflow-hidden bg-white shadow-sm">

                {/* Table Header */}
                <div className="grid grid-cols-2 bg-[#F6F7FB]">
                    <div className="p-6 border-r border-[#d9dce7]">
                        <h3 className="font-bebas text-2xl text-[#1a1a2e] text-center">
                            Manual Management (Spreadsheets)
                            <br />
                            <span className="text-[#082F8A]">
                                (Spreadsheets)
                            </span>
                        </h3>
                    </div>

                    <div className="p-6">
                        <h3 className="font-bebas text-2xl text-[#082F8A] text-center">
                            ScorePal Bracket Generator
                        </h3>
                    </div>
                </div>

                {/* Row 1 */}
                <div className="grid grid-cols-2 border-t border-[#d9dce7]">
                    <div className="p-5 border-r border-[#d9dce7] font-inter text-[#1a1a2e]/80">
                        Requires manual setup and formatting
                    </div>

                    <div className="p-5 font-inter text-[#1a1a2e] font-medium">
                        Instant bracket creation with automation
                    </div>
                </div>

                {/* Row 2 */}
                <div className="grid grid-cols-2 border-t border-[#d9dce7] bg-[#FAFBFD]">
                    <div className="p-5 border-r border-[#d9dce7] font-inter text-[#1a1a2e]/80">
                        High risk of errors in matchups and seeding
                    </div>

                    <div className="p-5 font-inter text-[#1a1a2e] font-medium">
                        Automated matchups with accurate structure
                    </div>
                </div>

                {/* Row 3 */}
                <div className="grid grid-cols-2 border-t border-[#d9dce7]">
                    <div className="p-5 border-r border-[#d9dce7] font-inter text-[#1a1a2e]/80">
                        Difficult to update and share changes
                    </div>

                    <div className="p-5 font-inter text-[#1a1a2e] font-medium">
                        Live updates and easy sharing
                    </div>
                </div>

                {/* Row 4 */}
                <div className="grid grid-cols-2 border-t border-[#d9dce7] bg-[#FAFBFD]">
                    <div className="p-5 border-r border-[#d9dce7] font-inter text-[#1a1a2e]/80">
                        Limited customization and scalability
                    </div>

                    <div className="p-5 font-inter text-[#1a1a2e] font-medium">
                        Flexible formats and full customization
                    </div>
                </div>

                {/* Row 5 */}
                <div className="grid grid-cols-2 border-t border-[#d9dce7]">
                    <div className="p-5 border-r border-[#d9dce7] font-inter text-[#1a1a2e]/80">
                        Time-consuming tournament management
                    </div>

                    <div className="p-5 font-inter text-[#1a1a2e] font-medium">
                        Efficient tournament bracket software with end-to-end management
                    </div>
                </div>
            </div>
        </div>

        <p className="font-inter text-[#1a1a2e]/80 text-base leading-relaxed text-center max-w-5xl mx-auto mt-10">
            Traditional spreadsheets often lead to time-consuming manual work and avoidable mistakes, whereas modern tournament management and automation tools streamline operations and make processes far more reliable.

            <a
                href="/sports-tournament-management-software/"
                className="text-[#082F8A] underline font-semibold"
            >

            </a>

              </p>

                </div>
              </section>

                    <section className="container mx-auto px-6 lg:px-12">
    <div className="bg-[#082F8A] rounded-3xl px-8 py-16 lg:px-16 lg:py-20 text-center">
      <h2 className="font-bebas text-white uppercase text-[clamp(32px,5vw,52px)] leading-tight font-bold tracking-wide mb-6">
        Create Tournament Brackets in Seconds with ScorePal
      </h2>

      <p className="font-inter text-white/90 text-base lg:text-lg leading-relaxed max-w-4xl mx-auto mb-6">
        ScorePal provides the quickest and easiest way to generate
        high-quality brackets for any type of competition, working much like
        intuitive <strong>tournament league software</strong>. Customize,
        print, share and run your event with confidence.
      </p>

      <p className="font-inter text-white text-lg font-semibold mb-10">
        Create your custom tournament brackets instantly with ScorePal today.
      </p>

      <div className="inline-flex items-center gap-2 bg-white text-[#082F8A] rounded-full px-5 py-2 font-semibold mb-10">
        <span className="font-inter text-sm lg:text-base">
          Best tournament bracket generator for clubs & organizers
        </span>
      </div>

      <div className="flex justify-center">
        <Link
          to={"/signup/player/" as any}
          className="inline-flex items-center gap-2 bg-white text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-gray-100 transition"
        >
          <span className="font-inter text-base font-medium">
            Lets Get Started
          </span>

          <div className="bg-[#082F8A] rounded-full p-1">
            <ArrowUpRightIcon className="text-white" size={28} />
          </div>
        </Link>
      </div>
  </div>

        </section>

                  <FaqComponent title={"FAQs About Tournament Brackets"} faqs={FAQS} />
            <FooterSection />
        </div>
    );
}
